import { useState } from "react";
import useSWR from "swr";
import { FileText, FileImage, FileSpreadsheet, FileVideo, Folder, Eye, Download, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { docUrl } from "@/lib/api";
import { fmtDate, fmtSize } from "@/lib/format";
import { SectionHeader, btn } from "@/components/common/Ornament";
import DocPreview from "@/components/documents/DocPreview";

const ICON = { pdf: FileText, docx: FileText, xlsx: FileSpreadsheet, csv: FileSpreadsheet, jpg: FileImage, jpeg: FileImage, png: FileImage, mp4: FileVideo };

export default function Documents() {
  const { role } = useAuth();
  const { data: folders = [] } = useSWR("/folders");
  const { data: docs = [] } = useSWR("/documents");
  const [sel, setSel] = useState("all");
  const [preview, setPreview] = useState(null);
  const isAdmin = role === "admin";

  const list = docs.filter((d) => sel === "all" || (sel === "unfiled" ? !d.folder_id : d.folder_id === sel));
  const count = (id) => docs.filter((d) => (id === "unfiled" ? !d.folder_id : d.folder_id === id)).length;
  const tabs = [{ id: "all", name: "All Documents", n: docs.length }, ...folders.map((f) => ({ id: f.id, name: f.name, n: count(f.id) })), ...(count("unfiled") ? [{ id: "unfiled", name: "Unfiled", n: count("unfiled") }] : [])];

  return (
    <div>
      <SectionHeader eyebrow="Data Room" title="Documents" testId="documents-header">Preview documents securely in your browser. Downloads are available only where the sponsor has enabled them.</SectionHeader>
      <div className="grid gap-6 lg:grid-cols-12">
        <aside className="lg:col-span-3 flex lg:flex-col gap-2 overflow-x-auto pb-2" data-testid="doc-folders">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setSel(t.id)} data-testid={`doc-folder-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`shrink-0 flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm text-left transition-colors ${sel === t.id ? "bg-[#0F2E23] text-[#F9F6F0]" : "bg-white border border-[#E2DCD2] hover:border-[#C59B27]"}`}>
              <span className="flex items-center gap-2"><Folder className={`h-4 w-4 ${sel === t.id ? "text-[#D4AF37]" : "text-[#C59B27]"}`} /> {t.name}</span>
              <span className="font-mono-num text-xs opacity-70">{t.n}</span>
            </button>
          ))}
        </aside>
        <div className="lg:col-span-9 space-y-3" data-testid="doc-list">
          {list.length === 0 && <p className="paper rounded-lg p-10 text-center text-slate-500">No documents in this folder yet.</p>}
          {list.map((d, i) => {
            const Icon = ICON[d.ext] || FileText;
            const canDl = d.downloadable || isAdmin;
            return (
              <div key={d.id} className="paper rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`doc-file-item-${i}`}>
                <div className="h-11 w-11 rounded-md bg-[#F9F6F0] border border-[#E2DCD2] flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#3E5C4E]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0F2E23] truncate">{d.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    <span className="uppercase font-mono-num">{d.ext}</span><span>{fmtSize(d.size)}</span><span>{fmtDate(d.created_at)}</span>
                    {isAdmin && !d.visible && <span className="inline-flex items-center gap-1 text-amber-700"><EyeOff className="h-3 w-3" /> Hidden from viewers</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPreview(d)} className={btn.primary + " !h-9"} data-testid={`doc-preview-btn-${i}`}><Eye className="h-4 w-4" /> Preview</button>
                  {canDl && <a href={docUrl(d.id, true)} className={btn.ghost + " !h-9"} data-testid={`doc-download-btn-${i}`}><Download className="h-4 w-4" /></a>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <DocPreview doc={preview} canDownload={preview && (preview.downloadable || isAdmin)} onClose={() => setPreview(null)} />
    </div>
  );
}
